import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { getAnchoredPaperSpacing } from '@components/v2/ui/utils/anchoredPaperSpacing';
import Box from '@mui/material/Box';
import { ChevronDown } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../../modules/translation/TranslationContext';
import { useMassActionsBarStyles } from '../../massActionsBar.styles';
import { MassActionsFilterConfig } from '../../massActionsBar.types';
import { useMassActionsSelectionMenu } from './hooks/useMassActionsSelectionMenu';

interface MassActionsSelectionMenuProps {
  totalSelected: number;
  toggleAllSelected: () => void;
  deselectAll?: () => void;
  massActionFilter?: MassActionsFilterConfig;
}

export function MassActionsSelectionMenu({
  totalSelected,
  toggleAllSelected,
  deselectAll,
  massActionFilter,
}: MassActionsSelectionMenuProps) {
  const { translate, locale } = useTranslation();
  const { classes } = useMassActionsBarStyles();
  const { isMenuOpen, anchorEl, openMenu, closeMenu, handleSelectAllResults, handleDeselectAll } =
    useMassActionsSelectionMenu({ toggleAllSelected, deselectAll, massActionFilter });

  return (
    <>
      <Button
        className={classes.actionButton}
        isActive={isMenuOpen}
        endIcon={
          <Box
            sx={{
              display: 'inline-flex',
              transition: 'transform 0.2s ease',
              transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Icon icon={ChevronDown} size="md" />
          </Box>
        }
        sx={(theme) => ({
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightRegular,
          lineHeight: 1,
        })}
        onClick={openMenu}
      >
        {translate(LangEnum.GLOBAL_SELECTED_COUNT, { quantity: totalSelected })}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: getAnchoredPaperSpacing({ variant: 'dropdown', horizontal: 'left' }) },
        }}
      >
        {massActionFilter?.enabled && (
          <MenuItem selected={massActionFilter.value} onClick={handleSelectAllResults}>
            {translate(LangEnum.CATALOGS_MASS_ACTIONS_SELECT_ALL, {
              totalResults: massActionFilter.totalResults.toLocaleString(locale),
            })}
          </MenuItem>
        )}
        <MenuItem onClick={handleDeselectAll}>{translate(LangEnum.GLOBAL_DESELECT_ALL)}</MenuItem>
      </Menu>
    </>
  );
}
